import { Loading, TableCard, TableOutput } from "@general-backoffice/core";
import type { PaginationMeta, ReferrerHierarchyStats } from "@investhinks/api-client";
import classNames from "classnames";
import get from "lodash/get";
import moment from "moment";
import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { Badge, Card } from "reactstrap";

import formatCurrency from "../../utils/formatCurrency";
import { TypedTable } from "../../utils/typedLib";
import PaginatedTableFooter from "../shared/PaginatedTableFooter";
import TriCheckbox from "../shared/TriCheckbox";

/** Keys accepted by `columnsToHide`, so a typo fails at compile time. */
export type ReferrerStatsColumn =
  | "number"
  | "badges"
  | "referrerType"
  | "referrals"
  | "activeBots"
  | "charged"
  | "pendingRewards"
  | "hierarchyCharged"
  | "activeBalance"
  | "inactiveBalance"
  | "totalProfit"
  | "totalLosses"
  | "signIn"
  | "buttons";

interface ReferrersStatsTableCardProps {
  referrers?: ReferrerHierarchyStats[];
  totalReferrers?: number;
  onReferrerClick?: (referrer: ReferrerHierarchyStats) => void;
  isLoading: boolean;
  pagination?: PaginationMeta | null;
  goToPage?: (page: number) => void;
  changePageSize?: (size: number) => void;
  filters?: Record<string, unknown>;
  changeFilters?: (key: string, value: unknown) => void;
  filtersToShow?: string[];
  sort?: Record<string, string | null>;
  changeSort?: (field: string, direction: string | null) => void;
  title?: string;
  columnsToHide?: ReferrerStatsColumn[];
  pageSize?: number;
}

interface MultiCurrencyOptions {
  containerClassName?: string;
}

type StatsField = keyof Pick<
  ReferrerHierarchyStats,
  "charged" | "activeBalance" | "inactiveBalance" | "totalProfit" | "totalLosses"
>;

const resolveNumericValue = (value: unknown, path: string): number => {
  if (typeof value === "number") return value;
  if (typeof value === "object" && value !== null) return Number(get(value, path, 0)) || 0;
  return 0;
};

const multiCurrencyContent =
  (field: StatsField, options: MultiCurrencyOptions = {}, path = "") =>
  (props: ReferrerHierarchyStats) => {
    const rawProp = props[field];
    const prop: Record<string, unknown> = typeof rawProp === "object" && rawProp !== null ? rawProp : {};
    if (!Object.keys(prop).length) return <div className="text-right pr-4">-</div>;

    // Get the main currency (EUR or the first one in the object)
    const mainCurr = prop?.EUR ? "EUR" : Object.keys(prop)?.[0];

    // Get the main value (EUR or the first one in the object)
    const mainValue = resolveNumericValue(prop[mainCurr], path);

    // Get the secondary currencies (all except the main one)
    const secondaryCurrencies = { ...prop };
    delete secondaryCurrencies[mainCurr];

    if (!mainCurr) return null;
    return (
      <div className={"text-right " + (options.containerClassName || "")}>
        <div className="font-weight-500">{formatCurrency(mainValue, mainCurr)}</div>
        {Object.entries(secondaryCurrencies).map(([currency, amount], i) => {
          if (!amount) return null;

          const value = resolveNumericValue(amount, path);
          return <div key={i}>{formatCurrency(value, currency)}</div>;
        })}
      </div>
    );
  };

const ReferrersStatsTableCard: React.FC<ReferrersStatsTableCardProps> = ({
  referrers = [],
  totalReferrers,
  onReferrerClick,
  isLoading,
  pagination,
  goToPage,
  changePageSize,
  filters,
  changeFilters,
  filtersToShow = ["search"],
  sort,
  changeSort,
  title,
  columnsToHide = [],
  pageSize = 10,
}) => {
  const { t } = useTranslation("adminStats", { keyPrefix: "referrersTable" });
  const navigate = useNavigate();
  const cardTitle = title ?? t("title");

  // Only advertise a column as sortable when a real handler was supplied. The
  // vendor table renders a pointer cursor and a sort arrow for any column that
  // defines `sort`, so the previous no-op default produced clickable headers
  // that did nothing on pages that never passed `changeSort`.
  const sortable = (field: string) =>
    changeSort ? { sort: (d: string | null) => changeSort(field, d), sortStatus: sort?.[field] } : {};

  const hideColumns = <T,>(field: ReferrerStatsColumn, column: T): T[] =>
    columnsToHide.includes(field) ? [] : [column];

  const columns = [
    ...hideColumns("number", {
      compressed: true,
      name: t("columns.number"),
      formattedContent: (_: ReferrerHierarchyStats, i: number) =>
        // Rank must reflect the page offset. With a page but no page size the
        // old expression silently restarted numbering from 1 on every page.
        (pagination ? pagination.pageSize * (pagination.page - 1) : 0) + i + 1,
      isTh: true,
    }),
    {
      compressed: true,
      name: t("columns.user"),
      formattedContent: ({ referrer }: ReferrerHierarchyStats) => (
        <>
          <div>{referrer?.name}</div>
          <small className="font-weight-bold">{referrer?.email}</small>
        </>
      ),
      isTh: true,
    },
    ...hideColumns("badges", {
      formattedContent: ({ referrer }: ReferrerHierarchyStats) => {
        const bookmarks: Parameters<typeof TableOutput.Bookmarks>[0]["bookmarks"] = [];
        if (!referrer) return null;
        if (referrer.isVIP) {
          bookmarks.push({
            icon: "VIP",
            title: "VIP",
            color: "warning",
          });
        }
        if (referrer.blocked) {
          bookmarks.push({
            icon: <i className="fas fa-lock" />,
            title: "User blocked",
            color: "danger",
          });
        }
        return <TableOutput.Bookmarks bookmarks={bookmarks} />;
      },
    }),
    ...hideColumns("referrerType", {
      name: t("columns.type"),
      formattedContent: ({ referrer }: ReferrerHierarchyStats) => {
        const referrerType = referrer?.referrerType || "";
        return (
          <span
            className={classNames("text-capitalize", {
              "font-weight-bold": referrerType !== "DEFAULT",
            })}
          >
            {referrerType.toLowerCase()}
          </span>
        );
      },
    }),
    ...hideColumns("referrals", {
      name: t("columns.referrals"),
      compressed: true,
      formattedContent: ({ referralsCount }: ReferrerHierarchyStats) => (
        <div style={{ fontSize: "1rem" }}>
          <Badge className="p-2">
            <span className="mr-1">{referralsCount || "0"}</span>
            <i className="fas fa-user text-default" />
          </Badge>
        </div>
      ),
      ...sortable("referralsCount"),
    }),
    ...hideColumns("activeBots", {
      name: t("columns.activeBots"),
      compressed: true,
      formattedContent: ({ activeBots, inactiveBots }: ReferrerHierarchyStats) => (
        <div style={{ fontSize: "1rem" }}>
          <Badge className="p-2" color="success">
            <span className="mr-1">{activeBots || "0"}</span>
            <i className="fas fa-robot" />
          </Badge>
          <Badge className="p-2" color="muted">
            <span className="mr-1">{inactiveBots || "0"}</span>
            <i className="fas fa-robot" />
          </Badge>
        </div>
      ),
      ...sortable("activeBots"),
    }),
    ...hideColumns("charged", {
      name: t("columns.charges"),
      compressed: true,
      formattedContent: multiCurrencyContent("charged", { containerClassName: "text-success" }, "total"),
      ...sortable("charged.EUR.total"),
    }),
    ...hideColumns("pendingRewards", {
      name: t("columns.pendingRewards"),
      compressed: true,
      formattedContent: ({ pendingRewards }: ReferrerHierarchyStats) => (
        <div className="text-right font-weight-500 text-success">
          {pendingRewards == null ? "—" : formatCurrency(pendingRewards, "EUR")}
        </div>
      ),
      ...sortable("pendingRewards"),
    }),
    ...hideColumns("hierarchyCharged", {
      name: (
        <span className="position-relative">
          Charges <small className="position-absolute bottom--3 left-0 text-lowercase">(+ Hierarchy)</small>
        </span>
      ),
      compressed: true,
      formattedContent: multiCurrencyContent("charged", { containerClassName: "text-success" }, "hierarchyTotal"),
      ...sortable("charged.EUR.hierarchyTotal"),
    }),
    ...hideColumns("activeBalance", {
      name: t("columns.activeBalance"),
      compressed: true,
      formattedContent: multiCurrencyContent("activeBalance", { containerClassName: "text-default" }),
      ...sortable("activeBalance.EUR"),
    }),
    ...hideColumns("inactiveBalance", {
      name: t("columns.inactiveBalance"),
      compressed: true,
      formattedContent: multiCurrencyContent("inactiveBalance", { containerClassName: "text-muted" }),
      ...sortable("inactiveBalance.EUR"),
    }),
    ...hideColumns("totalProfit", {
      name: t("columns.profit"),
      compressed: true,
      formattedContent: multiCurrencyContent("totalProfit", { containerClassName: "text-success" }),
      ...sortable("totalProfit.EUR"),
    }),
    ...hideColumns("totalLosses", {
      name: t("columns.losses"),
      compressed: true,
      formattedContent: multiCurrencyContent("totalLosses", { containerClassName: "text-danger" }),
      ...sortable("totalLosses.EUR"),
    }),
    ...hideColumns("signIn", {
      name: t("columns.signIn"),
      formattedContent: ({ referrer }: ReferrerHierarchyStats) => {
        const createdAt = referrer?.createdAt || "";
        return (
          <div className="text-center">
            <div className="small">{createdAt ? moment(createdAt).locale("en").format("DD MMM") : "-"}</div>
            <div>{createdAt ? moment(createdAt).format("YYYY") : ""}</div>
          </div>
        );
      },
    }),
    ...hideColumns("buttons", {
      compressed: true,
      formattedContent: (referrerStats: ReferrerHierarchyStats) => {
        const referredAffiliates = referrerStats?.referredAffiliates?.length;
        return (
          <TableOutput.CustomActions
            actions={[
              ...(onReferrerClick
                ? [
                    {
                      icon: <i className="fas fa-table text-default" />,
                      onClick: () => onReferrerClick(referrerStats),
                      description: "See referrer page",
                    },
                  ]
                : []),
              ...(referredAffiliates
                ? [
                    {
                      icon: (
                        <>
                          {referredAffiliates} <i className="fas fa-sitemap text-default" />
                        </>
                      ),
                      onClick: () => navigate(`/stats/affiliates/affiliates-hierarchy/${referrerStats?.referrer?.id}`),
                      description: "Referred affiliates",
                    },
                  ]
                : []),
            ]}
          />
        );
      },
    }),
  ];

  return (
    <Card className="overflow-hidden">
      <TableCard.Header
        title={cardTitle}
        subtitle={totalReferrers ? t("total", { count: totalReferrers }) : undefined}
        search={
          filtersToShow.includes("search")
            ? {
                value: String(filters?.["search"] ?? ""),
                onChange: (e: React.ChangeEvent<HTMLInputElement>) => changeFilters?.("search", e.target.value),
                msToChange: 1000,
                placeholder: t("searchPlaceholder"),
              }
            : undefined
        }
      >
        {!!filtersToShow.length && (
          <div className="px-4 flex-center justify-content-end flex-wrap" style={{ gap: ".8rem 1rem" }}>
            {filtersToShow.includes("isVIP") && (
              <TriCheckbox
                label={t("filters.vip")}
                state={typeof filters?.isVIP === "boolean" ? filters.isVIP : null}
                changeState={(s) => changeFilters?.("isVIP", s)}
                disabled={isLoading}
              />
            )}
          </div>
        )}
      </TableCard.Header>
      {isLoading && <Loading.Line />}
      <TypedTable
        columns={columns}
        rows={referrers}
        isLoading={isLoading}
        noContentMessage={t("noContent")}
        rowHeight={58}
        rowAmount={pagination?.pageSize ?? pageSize}
        onRowClick={onReferrerClick}
        selectable={false}
        sortable={false}
      />
      {pagination && goToPage && changePageSize && (
        <PaginatedTableFooter
          isLoading={isLoading}
          pagination={pagination}
          goToPage={goToPage}
          changePageSize={changePageSize}
        />
      )}
    </Card>
  );
};

export default ReferrersStatsTableCard;
